import inboundEmailKeys from '@/api/queries/inbound-emails/keyFactory';
import {
  InboundEmailInboxQuery,
  InboundEmailQuery,
  InboundEmailUnreadCountQuery,
} from '@/gql/graphql';
import {
  InfiniteData,
  QueryClient,
  QueryKey,
  UseMutationOptions,
} from '@tanstack/react-query';

type OptimisticContext = {
  previousInboxQueries: [
    QueryKey,
    InfiniteData<InboundEmailInboxQuery> | undefined,
  ][];
  previousDetailQuery: InboundEmailQuery | undefined;
  previousUnreadCount: InboundEmailUnreadCountQuery | undefined;
};

type OptimisticConfig<TVariables extends { inboundEmailId: string }> = {
  applyToInbox: (
    data: InfiniteData<InboundEmailInboxQuery>,
    variables: TVariables
  ) => { data: InfiniteData<InboundEmailInboxQuery>; wasUnread?: boolean };
  applyToDetail: (
    data: InboundEmailQuery,
    variables: TVariables
  ) => { data: InboundEmailQuery; wasUnread?: boolean };
  tracksUnreadCount: boolean;
};

export function createOptimisticMutationOptions<
  TData,
  TError,
  TVariables extends { inboundEmailId: string },
>(
  queryClient: QueryClient,
  config: OptimisticConfig<TVariables>,
  options?: Pick<
    UseMutationOptions<TData, TError, TVariables>,
    'onMutate' | 'onError' | 'onSettled'
  >
): {
  onMutate: (variables: TVariables) => Promise<OptimisticContext>;
  onError: (err: TError, variables: TVariables, context: unknown) => void;
  onSettled: (
    data: TData | undefined,
    error: TError | null,
    variables: TVariables,
    context: unknown
  ) => void;
} {
  return {
    onMutate: async (variables: TVariables) => {
      await Promise.all([
        queryClient.cancelQueries({
          queryKey: inboundEmailKeys.inbox(),
        }),
        queryClient.cancelQueries({
          queryKey: inboundEmailKeys.detail(variables.inboundEmailId),
        }),
      ]);

      const previousInboxQueries = queryClient.getQueriesData<
        InfiniteData<InboundEmailInboxQuery>
      >({
        queryKey: inboundEmailKeys.inbox(),
      });

      const previousDetailQuery = queryClient.getQueryData<InboundEmailQuery>(
        inboundEmailKeys.detail(variables.inboundEmailId)
      );

      const previousUnreadCount =
        queryClient.getQueryData<InboundEmailUnreadCountQuery>(
          inboundEmailKeys.unreadCount()
        );

      let wasUnread = false;

      queryClient.setQueriesData<InfiniteData<InboundEmailInboxQuery>>(
        { queryKey: inboundEmailKeys.inbox() },
        (oldData) => {
          if (!oldData) return oldData;
          const result = config.applyToInbox(oldData, variables);
          if (result.wasUnread) wasUnread = true;
          return result.data;
        }
      );

      queryClient.setQueryData<InboundEmailQuery>(
        inboundEmailKeys.detail(variables.inboundEmailId),
        (oldData) => {
          if (!oldData) return oldData;
          const result = config.applyToDetail(oldData, variables);
          if (result.wasUnread) wasUnread = true;
          return result.data;
        }
      );

      if (config.tracksUnreadCount && wasUnread) {
        queryClient.setQueryData<InboundEmailUnreadCountQuery>(
          inboundEmailKeys.unreadCount(),
          (old) => {
            if (!old) return old;
            return {
              ...old,
              me: {
                ...old.me,
                inboundEmailUnreadCount: Math.max(
                  0,
                  old.me.inboundEmailUnreadCount - 1
                ),
              },
            };
          }
        );
      }

      await options?.onMutate?.(variables);

      return {
        previousInboxQueries,
        previousDetailQuery,
        previousUnreadCount,
      };
    },

    onError: (err: TError, variables: TVariables, context: unknown) => {
      const ctx = context as OptimisticContext | undefined;

      ctx?.previousInboxQueries.forEach(([queryKey, oldData]) => {
        queryClient.setQueryData(queryKey, oldData);
      });

      if (ctx?.previousDetailQuery) {
        queryClient.setQueryData(
          inboundEmailKeys.detail(variables.inboundEmailId),
          ctx.previousDetailQuery
        );
      }

      if (config.tracksUnreadCount && ctx?.previousUnreadCount) {
        queryClient.setQueryData(
          inboundEmailKeys.unreadCount(),
          ctx.previousUnreadCount
        );
      }

      options?.onError?.(err, variables, context);
    },

    onSettled: (
      data: TData | undefined,
      error: TError | null,
      variables: TVariables,
      context: unknown
    ) => {
      void queryClient.invalidateQueries({
        queryKey: inboundEmailKeys.lists(),
      });
      void queryClient.invalidateQueries({
        queryKey: inboundEmailKeys.detail(variables.inboundEmailId),
      });

      options?.onSettled?.(data, error, variables, context);
    },
  };
}
